import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

// Get price from product - handles both array of numbers and array of objects
const getProductPrice = (product) => {
    if (!product.precios || product.precios.length === 0) return 0;
    const firstPrice = product.precios[0];
    if (typeof firstPrice === 'number') return firstPrice;
    if (typeof firstPrice === 'object' && firstPrice?.precio) return firstPrice.precio;
    return 0;
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    
    // Use ref to avoid stale closure issues with removeItem
    const removeItemRef = useRef(null);

    const removeItem = useCallback((productId) => {
        setItems(prev => prev.filter(item => item.product_id !== productId));
    }, []);
    
    // Keep ref updated
    removeItemRef.current = removeItem;

    const addItem = useCallback((product, quantity = 1) => {
        setItems(prev => {
            const existingIndex = prev.findIndex(item => item.product_id === product.product_id);
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    quantity: updated[existingIndex].quantity + quantity
                };
                return updated;
            }
            return [...prev, { ...product, quantity }];
        });
    }, []);

    const updateQuantity = useCallback((productId, quantity) => {
        if (quantity <= 0) {
            removeItemRef.current?.(productId);
            return;
        }
        setItems(prev => prev.map(item =>
            item.product_id === productId ? { ...item, quantity } : item
        ));
    }, []);

    const clearCart = useCallback(() => {
        setItems([]);
    }, []);

    const getItemQuantity = useCallback((productId) => {
        const item = items.find(i => i.product_id === productId);
        return item ? item.quantity : 0;
    }, [items]);

    const totalItems = useMemo(() => {
        return items.reduce((sum, item) => sum + item.quantity, 0);
    }, [items]);

    const totalPrice = useMemo(() => {
        return items.reduce((sum, item) => {
            const price = getProductPrice(item);
            return sum + (price * item.quantity);
        }, 0);
    }, [items]);

    const value = useMemo(() => ({
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getItemQuantity,
        totalItems,
        totalPrice
    }), [items, addItem, removeItem, updateQuantity, clearCart, getItemQuantity, totalItems, totalPrice]);

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
